import React from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Circle, G, Text } from 'react-native-svg';
import ThemedText from './ThemedText';
import { useTheme } from '../../theme/ThemeContext';

export interface PieChartData {
  value: number;
  color: string;
  label: string;
}

interface PieChartProps {
  data: PieChartData[];
  size?: number;
  strokeWidth?: number;
  showLabels?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({ data, size = 120, strokeWidth = 20, showLabels = true }) => {
  const { theme } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  if (total === 0) {
    return (
      <View style={[styles.container, { width: size, height: size }]}>
        <View style={[styles.emptyCircle, { 
          width: size, 
          height: size, 
          borderRadius: size / 2,
          borderWidth: strokeWidth,
          borderColor: theme.border,
        }]} />
        <ThemedText style={[styles.noDataText, { color: theme.textSecondary }]}>
          No data
        </ThemedText>
      </View>
    );
  }

  let currentAngle = -90; // Start from top
  const labelRadius = radius + strokeWidth / 2 + 15;

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      <Svg width={size} height={size}>
        <G rotation={0} origin={`${center}, ${center}`}>
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const strokeDashoffset = circumference - (circumference * percentage) / 100;
            
            // Calculate label position (middle of the segment)
            const segmentAngle = (percentage / 100) * 360;
            const labelAngle = currentAngle + segmentAngle / 2;
            const labelAngleRad = (labelAngle * Math.PI) / 180;
            const labelX = center + labelRadius * Math.cos(labelAngleRad);
            const labelY = center + labelRadius * Math.sin(labelAngleRad);
            
            currentAngle += segmentAngle;
            
            return (
              <G key={index}>
                <Circle
                  cx={center}
                  cy={center}
                  r={radius}
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  rotation={currentAngle - segmentAngle}
                  origin={`${center}, ${center}`}
                  strokeLinecap="round"
                />
                {showLabels && percentage > 8 && (
                  <Text
                    x={labelX}
                    y={labelY}
                    fontSize="10"
                    fontWeight="700"
                    fill="#FFF"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {Math.round(percentage)}%
                  </Text>
                )}
              </G>
            );
          })}
        </G>
      </Svg>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyCircle: {
    position: 'absolute',
  },
  noDataText: {
    fontSize: 12,
    fontWeight: '500',
  },
});

export default PieChart;
